/**
 * O contrato do teste de modelo, compartilhado pelas telas que escolhem provedor de IA.
 *
 * Vivia no serviço da organização, que era a única tela a escolher modelos. A partir do
 * AB#85605 a plataforma escolhe pelas mesmas regras, e os dois lados precisam ler o MESMO
 * relatório — o front traduz `reason` e `route_source` por chave de i18n, então duas
 * definições divergentes virariam texto faltando em produção, não erro de build.
 */

/** Rota que a chamada usou. `embedding` é `/v1/embeddings`; `transcription`, `/v1/audio/transcriptions`. */
export type AiModelRoute = "chat" | "responses" | "embedding" | "transcription" | "web_search";

/** De onde saiu a rota — é o que diferencia typo no cadastro de ausência. */
export type AiModelRouteSource = "declared" | "default" | "invalid_declaration";

export type AiModelFailureReason =
  | "route_rejected"
  | "invalid_credential"
  | "model_not_found"
  | "provider_unavailable"
  | "gateway_rejected"
  | "unexpected"
  // Busca web (AB#86885): a integração não declara a capacidade, o engine não respondeu,
  // ou o provedor de busca falhou.
  | "not_web_search"
  | "engine_unavailable"
  | "provider_failed";

export type AiModelOutcome =
  | { status: "skipped" }
  | {
      status: "ok";
      model: string;
      route: AiModelRoute;
      route_source: AiModelRouteSource;
    }
  | {
      status: "failed";
      model: string;
      route: AiModelRoute;
      route_source: AiModelRouteSource;
      reason: AiModelFailureReason;
      detail: string;
    };

/** Os motivos pelos quais o relatório pode vir sem nada testado. */
export type AiModelTestUnavailable =
  | "no_default_model"
  | "integration_not_found"
  | "not_llm"
  | "creds_failed"
  | "gateway_not_configured"
  | "not_found";

export type AiModelTestReport =
  | { status: AiModelTestUnavailable }
  | {
      status: "ok";
      integration_base_id: string;
      integration_name: string;
      conversational: AiModelOutcome;
      embedding: AiModelOutcome;
      /** Só a tela da plataforma escolhe transcrição; para a organização vem `skipped`. */
      transcription: AiModelOutcome;
      /** Só no teste da finalidade de busca web, que vem sozinho (AB#86885). */
      web_search?: AiModelOutcome;
    };

/**
 * O estado do veredito de UM campo na tela.
 *
 * `unreachable` e `unavailable` existem separados de `done` porque não dizem nada sobre o
 * modelo: a chamada não aconteceu, ou o relatório veio sem gateway configurado. Tratá-los
 * como reprovação acusaria o cadastro por uma falha de rede.
 */
export type FieldCheck =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "done"; outcome: AiModelOutcome }
  | { state: "unreachable" }
  | { state: "unavailable"; reason: string };

export const aprovado = (check: FieldCheck) =>
  check.state === "done" && check.outcome.status === "ok";

/**
 * Quantos elos a cadeia de contingência aceita, além do principal (AB#85617).
 *
 * Espelha o teto do servidor, que é quem de fato recusa. Existe aqui para a tela parar de
 * oferecer "acrescentar" antes de o salvamento falhar — descobrir o limite por recusa é
 * pior que não poder chegar nele.
 */
export const PROFUNDIDADE_MAXIMA_CONTINGENCIA = 3

/**
 * A escolha de UMA finalidade: por qual integração ela sai, e com que modelo.
 *
 * Vive aqui, e não no acordeão que a desenha, porque é o formato que a tela da organização,
 * a da plataforma e a cadeia de contingência compartilham — três telas e um componente.
 */
export type PurposeChoice = {
  integrationId: string | null;
  model: string;
};

/**
 * Um nível da cadeia de contingência, com identidade própria.
 *
 * O `id` não viaja para o servidor — lá a ordem da lista é toda a identidade que existe.
 * Ele está aqui porque o React precisa saber QUEM é cada nível entre uma renderização e
 * outra: pela posição, remover o do meio faz o de baixo herdar o estado do removido —
 * catálogo e veredito da escolha errada — até a sonda refazer e corrigir a tela sozinha.
 */
export type NivelDaCadeia<P extends string> = {
  id: string;
  escolhas: Record<P, PurposeChoice>;
};

/**
 * Um nível novo, em branco ou já preenchido com o que está gravado.
 *
 * Uma função só para os dois casos: quem monta a cadeia a partir do servidor e quem
 * acrescenta um nível vazio precisam da mesma identidade nova, e duas formas de criar nível
 * seriam duas chances de esquecer o `id`.
 */
export function nivelVazio<P extends string>(
  purposes: readonly P[],
  escolhas?: Record<P, PurposeChoice>
): NivelDaCadeia<P> {
  return {
    id: crypto.randomUUID(),
    escolhas:
      escolhas ??
      purposes.reduce(
        (acc, p) => ({ ...acc, [p]: { integrationId: null, model: "" } as PurposeChoice }),
        {} as Record<P, PurposeChoice>
      ),
  };
}
